import { Modal, Spin, Tag } from "@arco-design/web-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isGenericHttpFailureMessage, normalizeHttpError } from "../../../lib/http/errors";
import { formatMoney, versionStatusText } from "../service/quoteBudgetEditorViewService";
import type { QuoteBudgetCostBreakdown, QuoteBudgetSummary, QuoteBudgetVersion } from "../types";

const COST_BUCKET_LABELS: Record<QuoteBudgetCostBreakdown["buckets"][number]["key"], string> = {
  student: "学生费用",
  teacher: "老师费用",
  guide: "导游费用",
  driver: "司机费用",
  unassigned: "公共及摊销",
};

/** 版本切换失败使用稳定中文，同时保留用户可理解的业务提示。 */
function versionLoadErrorText(error: unknown): string {
  const normalized = normalizeHttpError(error);
  if (normalized.statusCode === 403) return "当前账号没有切换核价版本的权限，请联系管理员。";
  if (normalized.code === "QUOTE_BUDGET_VERSION_CONFLICT") {
    return "当前核价版本已发生变化，请刷新页面后重试。";
  }
  if (normalized.code === "QUOTE_BUDGET_SCHEMA_NOT_READY") {
    return "核价数据库结构尚未完成升级，请联系管理员完成升级后重试。";
  }
  const message = normalized.message.trim();
  const hasChinese = /[\u3400-\u9fff]/u.test(message);
  const containsTechnicalDetail = /https?:\/\/|trace|stack|exception|gateway|timeout|sql/i.test(message);
  if (message && !isGenericHttpFailureMessage(message) && hasChinese && !containsTechnicalDetail && message.length <= 120) {
    return `版本加载失败：${message}`;
  }
  return "版本加载失败，请稍后重试。";
}

/** 将版本弹窗挂到编辑器，避免 Arco 锁定 body 时引起页面横向位移。 */
function getQuoteBudgetPopupContainer(): HTMLElement {
  return document.querySelector<HTMLElement>(".quote-budget-editor") ?? document.body;
}

/** 版本切换面板属性。当前版本只读高亮，点击其他版本跳转到该版本编辑页. */
export type QuoteVersionPanelProps = {
  versions: QuoteBudgetVersion[];
  currentBudgetOrderId: string;
  /** 当前编辑器请求代次，用于识别 A→B→A 后旧 A 请求。 */
  contextGeneration?: number;
  summary?: QuoteBudgetSummary | null;
  costBreakdown?: QuoteBudgetCostBreakdown;
  auditPending?: boolean;
  unpricedLineCount?: number;
  onLocateRisk?: () => void;
  teacherPays?: boolean;
  isDirty?: boolean;
  /** 快速录入区存在尚未加入明细的文本时，切换确认文案需明确其会被丢弃. */
  hasPendingEntryText?: boolean;
  /** 保存、定稿、复制等全局写请求进行中时禁止切换版本。 */
  disabled?: boolean;
  onLoadVersion?: (budgetOrderId: string) => Promise<unknown>;
  /** 目标版本已完成业务确认后的导航出口。 */
  onNavigateVersion?: (budgetOrderId: string) => void;
};

type VersionSwitchError = {
  targetBudgetOrderId: string;
  message: string;
};

/**
 * 核价版本切换列表：按版本号倒序展示，最新版带标记，点击非当前版本导航到其编辑页。
 * 仅一个版本时保留成本摘要，不渲染切换列表。
 */
export function QuoteVersionPanel({
  versions,
  currentBudgetOrderId,
  contextGeneration = 0,
  summary,
  costBreakdown,
  auditPending = false,
  unpricedLineCount = 0,
  onLocateRisk,
  teacherPays,
  isDirty = false,
  hasPendingEntryText = false,
  disabled = false,
  onLoadVersion = async () => undefined,
  onNavigateVersion,
}: QuoteVersionPanelProps) {
  const navigate = useNavigate();
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null);
  const [switchingVersionId, setSwitchingVersionId] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<VersionSwitchError | null>(null);
  const mountedRef = useRef(false);
  const attemptRef = useRef(0);
  const contextRef = useRef({ budgetOrderId: currentBudgetOrderId, generation: contextGeneration });
  const currentContext = { budgetOrderId: currentBudgetOrderId, generation: contextGeneration };
  if (
    contextRef.current.budgetOrderId !== currentContext.budgetOrderId
    || contextRef.current.generation !== currentContext.generation
  ) {
    contextRef.current = currentContext;
    attemptRef.current += 1;
  }
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      attemptRef.current += 1;
    };
  }, []);
  useEffect(() => {
    setPendingVersionId(null);
    setSwitchingVersionId(null);
    setSwitchError(null);
  }, [contextGeneration, currentBudgetOrderId]);
  if (versions.length < 2 && !summary) {
    return null;
  }
  const sorted = [...versions].sort((a, b) => b.versionNo - a.versionNo);
  const switchingVersion = versions.find((version) => version.budgetOrderId === switchingVersionId);
  const hasBlockingAudit = Boolean(
    costBreakdown?.auditIssues.some((issue) => issue.severity === "error"),
  );

  /** dirty 时先确认放弃未保存修改，否则直接进入版本预加载。 */
  function requestSwitchVersion(targetBudgetOrderId: string) {
    if (disabled || switchingVersionId || pendingVersionId) return;
    if (isDirty || hasPendingEntryText) {
      setPendingVersionId(targetBudgetOrderId);
      return;
    }
    void switchVersion(targetBudgetOrderId);
  }

  /** 先加载目标版本再更新路由，失败时保留当前页面。 */
  async function switchVersion(targetBudgetOrderId: string) {
    if (switchingVersionId) return;
    const request = {
      attempt: ++attemptRef.current,
      context: { ...contextRef.current },
    };
    const isCurrentRequest = () => (
      mountedRef.current
      && attemptRef.current === request.attempt
      && contextRef.current.budgetOrderId === request.context.budgetOrderId
      && contextRef.current.generation === request.context.generation
    );
    setSwitchError(null);
    setSwitchingVersionId(targetBudgetOrderId);
    try {
      await onLoadVersion(targetBudgetOrderId);
      if (!isCurrentRequest()) return;
      if (onNavigateVersion) {
        onNavigateVersion(targetBudgetOrderId);
      } else {
        navigate(`/quote/budgets/${targetBudgetOrderId}`);
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      setSwitchError({
        targetBudgetOrderId,
        message: versionLoadErrorText(error),
      });
    } finally {
      if (isCurrentRequest()) setSwitchingVersionId(null);
    }
  }

  return (
    <section className="quote-budget-version-panel" aria-label="行程核价版本">
      <div className="quote-budget-version-panel__title">行程核价版本</div>
      {summary ? (
        <div className="quote-budget-version-summary" aria-label="当前版本成本">
          <div className="quote-budget-version-summary__primary">
            <span>{unpricedLineCount > 0 ? "已核价小计（成本未完整）" : "当前版本成本"}</span>
            <em>{formatMoney(summary.perCapitaCostAmount)}/人</em>
            <strong>{formatMoney(summary.totalCostAmount)}</strong>
            <small>{teacherPays ? "老师出钱，按学生和老师分摊" : "老师不出钱，按学生分摊"}</small>
            {unpricedLineCount > 0 ? <small role="note">尚有 {unpricedLineCount} 条待补价，当前总额和人均费用未包含完整成本。{onLocateRisk ? <button type="button" onClick={onLocateRisk}>查看待处理明细</button> : null}</small> : null}
          </div>
          {costBreakdown ? (
            <>
              <div className="quote-budget-version-summary__snapshot-label">成本构成</div>
              <dl className="quote-budget-version-summary__breakdown">
                <div>
                  <dt>普通费用</dt>
                  <dd className="quote-budget-version-summary__money">
                    {formatMoney(costBreakdown.ordinaryAmount)}
                  </dd>
                </div>
                <div>
                  <dt>营业税</dt>
                  <dd className="quote-budget-version-summary__money">
                    {formatMoney(costBreakdown.businessTaxAmount)}
                  </dd>
                </div>
              </dl>
              <dl className="quote-budget-version-summary__roles" aria-label="角色费用构成">
                {costBreakdown.buckets
                  .filter((bucket) => Number(bucket.amount) !== 0 || bucket.lineCount > 0)
                  .map((bucket) => (
                    <div key={bucket.key}>
                      <dt>{COST_BUCKET_LABELS[bucket.key]}</dt>
                      <dd className="quote-budget-version-summary__money">
                        {formatMoney(bucket.amount)}
                      </dd>
                    </div>
                  ))}
              </dl>
              {costBreakdown.auditIssues.length > 0 ? (
                <div
                  className={`quote-budget-version-summary__audit${hasBlockingAudit ? " is-error" : ""}`}
                  role="alert"
                >
                  <strong>核价审计</strong>
                  {auditPending ? <p>以下为上次保存的审计结果；本地修改尚未重新核验，请保存后复核。</p> : null}
                  {onLocateRisk ? <button type="button" onClick={onLocateRisk}>定位问题明细</button> : null}
                  <ul>
                    {costBreakdown.auditIssues.map((issue, index) => (
                      <li
                        key={`${issue.code}-${issue.budgetLineId ?? index}`}
                        data-severity={issue.severity}
                      >
                        <span>{issue.severity === "error" ? "阻断" : "提醒"}</span>
                        {issue.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      {versions.length >= 2 ? (
        <div className="quote-budget-version-panel__list">
          {sorted.map((version) => {
          const isCurrent = version.budgetOrderId === currentBudgetOrderId;
          return (
            <button
              key={version.budgetOrderId}
              type="button"
              className={`quote-budget-version-option${isCurrent ? " is-current" : ""}`}
              aria-current={isCurrent ? "true" : undefined}
              disabled={disabled || isCurrent || Boolean(switchingVersionId) || Boolean(pendingVersionId)}
              onClick={() => requestSwitchVersion(version.budgetOrderId)}
            >
              <span className="quote-budget-version-option__head">
                <strong>V{version.versionNo}</strong>
                <span>{version.versionName}</span>
                {version.isLatest ? (
                  <Tag color="arcoblue" size="small">
                    最新
                  </Tag>
                ) : null}
              </span>
              <span className="quote-budget-version-option__meta">
                <em>{versionStatusText(version.status)}</em>
                <strong>{formatMoney(version.costBudgetTotalAmount)}</strong>
              </span>
            </button>
          );
          })}
        </div>
      ) : null}
      <Modal
        visible={Boolean(pendingVersionId)}
        getPopupContainer={getQuoteBudgetPopupContainer}
        simple
        className="quote-budget-version-dialog"
        style={{ width: 400, maxWidth: "calc(100vw - 32px)" }}
        title="有未保存修改"
        okText="确认切换"
        cancelText="取消"
        maskClosable={false}
        onOk={() => {
          const targetBudgetOrderId = pendingVersionId;
          setPendingVersionId(null);
          if (targetBudgetOrderId) void switchVersion(targetBudgetOrderId);
        }}
        onCancel={() => setPendingVersionId(null)}
      >
        {hasPendingEntryText
          ? "快速录入区有未加入内容。切换版本将丢弃这些内容，是否继续？"
          : "切换版本将放弃当前未保存修改，是否继续？"}
      </Modal>
      <Modal
        visible={Boolean(switchingVersionId)}
        getPopupContainer={getQuoteBudgetPopupContainer}
        simple
        className="quote-budget-version-dialog quote-budget-version-loading-modal"
        style={{
          width: 360,
          maxWidth: "calc(100vw - 32px)",
          opacity: 1,
          transform: "none",
          transition: "none",
        }}
        maskStyle={{
          backgroundColor: "rgba(29, 29, 31, 0.32)",
          opacity: 1,
          transition: "none",
        }}
        title={`正在切换到 V${switchingVersion?.versionNo ?? ""}`}
        footer={null}
        maskClosable={false}
        escToExit={false}
        closable={false}
        onCancel={() => undefined}
      >
        <div className="quote-budget-version-loading-status">
          <Spin loading />
          <span>数据准备完成后自动打开</span>
        </div>
      </Modal>
      <Modal
        visible={Boolean(switchError)}
        getPopupContainer={getQuoteBudgetPopupContainer}
        simple
        className="quote-budget-version-dialog"
        style={{ width: 360, maxWidth: "calc(100vw - 32px)" }}
        title="版本加载失败"
        okText="重试加载"
        cancelText="关闭"
        okButtonProps={{ disabled }}
        maskClosable={false}
        onOk={() => {
          const targetBudgetOrderId = switchError?.targetBudgetOrderId;
          if (!targetBudgetOrderId || disabled) return;
          setSwitchError(null);
          void switchVersion(targetBudgetOrderId);
        }}
        onCancel={() => setSwitchError(null)}
      >
        {switchError?.message}
      </Modal>
    </section>
  );
}
